import { Link } from 'react-router-dom';
import { Star, Users, Home as HomeIcon, Award, Zap, Heart, Phone, MapPin, Calendar, Shield, Target, TrendingUp, Activity, Brain, Baby, Stethoscope, Ambulance, UserCheck, ArrowRight, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import './Home.css';

const Home = () => {
  const whatsappUrl = 'https://wa.me/919886332528';

  const services = [
  {
    icon: <Activity size={32} />,
    title: 'Orthopaedic Physiotherapy',
    description: 'Specialized treatment for bone, joint, and muscle conditions',
    link: '/services/orthopaedic'
  },
  {
    icon: <TrendingUp size={32} />,
    title: 'Sports Injury Rehabilitation',
    description: 'Get back to peak performance with expert sports physio',
    link: '/services/sports-injury'
  },
  {
    icon: <Brain size={32} />,
    title: 'Neurological Physiotherapy',
    description: 'Expert care for stroke, paralysis, and neurological conditions',
    link: '/services/neurological'
  },
  {
    icon: <Stethoscope size={32} />,
    title: 'Post-Surgical Rehabilitation',
    description: 'Comprehensive recovery programs after surgery',
    link: '/services/post-surgical'
  },
  {
    icon: <Baby size={32} />,
    title: 'Paediatric Physiotherapy',
    description: 'Gentle, effective treatment for children',
    link: '/services/paediatric'
  },
  {
    icon: <HomeIcon size={32} />,
    title: 'Home Visit Physiotherapy',
    description: 'Expert physiotherapy at your doorstep across Goa',
    link: '/services/home-visit'
  }];


  const whyChooseUs = [
  {
    icon: <Zap size={40} />,
    title: 'Advanced Equipment',
    description: 'Latest physiotherapy machines for faster recovery'
  },
  {
    icon: <Users size={40} />,
    title: 'Expert Team',
    description: 'Certified physiotherapists specialising in every area'
  },
  {
    icon: <HomeIcon size={40} />,
    title: 'Home Visits Across Goa',
    description: 'We come to you anywhere in Goa'
  },
  {
    icon: <Heart size={40} />,
    title: 'Personalised Care',
    description: 'Every treatment plan is tailored to you'
  }];


  const conditions = [
  { name: 'Back Pain', icon: <Activity size={24} /> },
  { name: 'Neck Pain', icon: <Activity size={24} /> },
  { name: 'Knee Pain', icon: <Activity size={24} /> },
  { name: 'Post-Surgery Rehab', icon: <Stethoscope size={24} /> },
  { name: 'Sports Injuries', icon: <TrendingUp size={24} /> },
  { name: 'Stroke & Paralysis', icon: <Brain size={24} /> },
  { name: 'Sciatica', icon: <Activity size={24} /> },
  { name: 'Frozen Shoulder', icon: <Activity size={24} /> },
  { name: 'Elderly Care', icon: <Heart size={24} /> },
  { name: 'Fracture Recovery', icon: <Shield size={24} /> }];


  const equipment = [
  {
    title: 'TENS / IFT Therapy',
    description: 'Advanced electrical stimulation for pain relief',
    image: 'https://images.pexels.com/photos/30483052/pexels-photo-30483052.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  },
  {
    title: 'Laser Therapy',
    description: 'Modern laser treatment for faster healing',
    image: 'https://images.pexels.com/photos/7772648/pexels-photo-7772648.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  },
  {
    title: 'Ultrasound Therapy',
    description: 'Deep tissue healing with ultrasound technology',
    image: 'https://images.pexels.com/photos/13697729/pexels-photo-13697729.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  }];


  const testimonials = [
  {
    name: 'Rajesh Kumar',
    rating: 5,
    text: 'Dr. Manaz and his team helped me recover from severe back pain. The treatment was excellent and the staff is very professional. Highly recommend!'
  },
  {
    name: 'Priya Desai',
    rating: 5,
    text: 'Best physiotherapy clinic in Panjim! The equipment is top-notch and the therapists are highly skilled. My knee pain is completely gone.'
  },
  {
    name: 'Antonio Fernandes',
    rating: 5,
    text: 'Had post-surgery rehabilitation here. The personalized care and attention I received was outstanding. Thank you for helping me walk again!'
  },
  {
    name: 'Sunita Naik',
    rating: 5,
    text: 'The home visit service is a blessing for elderly patients like my mother. The physiotherapist is punctual, professional, and caring.'
  },
  {
    name: 'Vikram Rao',
    rating: 5,
    text: 'Recovered from a sports injury much faster than expected. The team understands athletes and their needs. Excellent clinic!'
  }];


  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="hero-text">
            <h1>Expert Physiotherapy & Rehabilitation in Panjim, Goa</h1>
            <p className="hero-subtitle">
              Advanced Treatments • Home Visits Available • Best-Equipped Clinic in Miramar
            </p>
            <div className="hero-buttons">
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="btn-primary">
                <Phone size={20} />
                Book Appointment on WhatsApp
              </a>
              <Link to="/home-visit-physiotherapy-panjim" className="btn-secondary">
                <HomeIcon size={20} />
                Request a Home Visit
              </Link>
            </div>
            <div className="trust-strip">
              <div className="trust-item">
                <Star size={18} fill="currentColor" />
                <span>5 Star Rated</span>
              </div>
              <div className="trust-item">
                <Users size={18} />
                <span>1000+ Patients Treated</span>
              </div>
              <div className="trust-item">
                <HomeIcon size={18} />
                <span>Home Visits</span>
              </div>
              <div className="trust-item">
                <MapPin size={18} />
                <span>Miramar, Panjim</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section about-section">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2>Panjim's Most Trusted Physiotherapy Clinic</h2>
              <p>
                At Dr. Manaz's Physiotherapy & Rehabilitation Clinic in Miramar, Panjim, we combine expert hands-on therapy with the most advanced physiotherapy equipment available in Goa. Whether you visit our clinic or need a home visit, our certified team is here to help you recover faster and live pain-free.
              </p>
              <Link to="/our-team" className="btn-primary">
                <Users size={20} />
                Meet Our Team
              </Link>
            </div>
            <div className="about-image">
              <img src="https://images.unsplash.com/photo-1630226040750-d934f017f0e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxwaHlzaW90aGVyYXB5JTIwY2xpbmljfGVufDB8fHx8MTc4MDQ3NjEzN3ww&ixlib=rb-4.1.0&q=85" alt="Dr. Manaz Physiotherapy Clinic" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section services-section">
        <div className="container">
          <div className="section-header">
            <h2>Our Physiotherapy Services</h2>
            <p>Comprehensive treatment for all your rehabilitation needs</p>
          </div>
          <div className="services-grid">
            {services.map((service, index) =>
            <Card key={index} className="service-card">
                <CardContent className="service-card-content">
                  <div className="service-icon">{service.icon}</div>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <Link to={service.link} className="service-link">
                    Learn More <ArrowRight size={16} />
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section why-section">
        <div className="container">
          <div className="section-header">
            <h2>Why Choose Dr. Manaz's Clinic?</h2>
          </div>
          <div className="why-grid">
            {whyChooseUs.map((item, index) =>
            <div key={index} className="why-card">
                <div className="why-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Home Visits Feature */}
      <section className="section-dark home-visits-feature">
        <div className="container">
          <div className="home-visits-content">
            <h2>Can't Come to Us? We Come to You.</h2>
            <p className="feature-subtitle">
              Certified physiotherapists for home visits in Panjim, Porvorim, Mapusa, Candolim, Calangute, and all of Goa.
            </p>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="btn-primary large">
              <HomeIcon size={20} />
              Request a Home Visit on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Conditions We Treat */}
      <section className="section conditions-section">
        <div className="container">
          <div className="section-header">
            <h2>Conditions We Treat</h2>
            <p>Expert treatment for a wide range of physical conditions</p>
          </div>
          <div className="conditions-grid">
            {conditions.map((condition, index) =>
            <div key={index} className="condition-card">
                <div className="condition-icon">{condition.icon}</div>
                <span>{condition.name}</span>
              </div>
            )}
          </div>
          <div className="section-cta">
            <Link to="/conditions" className="btn-secondary">
              View All Conditions
            </Link>
          </div>
        </div>
      </section>

      {/* Equipment Highlight */}
      <section className="section equipment-section">
        <div className="container">
          <div className="section-header">
            <h2>Panjim's Best-Equipped Physiotherapy Clinic</h2>
            <p>Advanced technology for faster, more effective treatment</p>
          </div>
          <div className="equipment-grid">
            {equipment.map((item, index) =>
            <Card key={index} className="equipment-card">
                <div className="equipment-image">
                  <img src={item.image} alt={item.title} />
                </div>
                <CardContent>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </CardContent>
              </Card>
            )}
          </div>
          <div className="section-cta">
            <Link to="/equipment" className="btn-primary">
              See All Equipment
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section testimonials-section">
        <div className="container">
          <div className="section-header">
            <h2>What Our Patients Say</h2>
            <p>Real experiences from people we've helped</p>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) =>
            <Card key={index} className="testimonial-card">
                <CardContent className="testimonial-content">
                  <div className="stars">
                    {[...Array(testimonial.rating)].map((_, i) =>
                  <Star key={i} size={16} fill="currentColor" />
                  )}
                  </div>
                  <p className="testimonial-text">"{testimonial.text}"</p>
                  <p className="testimonial-name">— {testimonial.name}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Contact Strip */}
      <section className="section contact-strip-section">
        <div className="container">
          <div className="contact-strip-grid">
            <div className="map-container">
              <iframe
                title="Dr. Manaz Physiotherapy Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3844.5!2d73.8!3d15.5!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTXCsDMwJzAwLjAiTiA3M8KwNDgnMDAuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy">
              </iframe>
            </div>
            <div className="contact-info">
              <h3>Visit Our Clinic</h3>
              <div className="contact-details">
                <div className="detail-item">
                  <MapPin size={20} />
                  <div>
                    <strong>Address</strong>
                    <p>House no, H-4, Miramar, Panaji, Goa 403001</p>
                  </div>
                </div>
                <div className="detail-item">
                  <Phone size={20} />
                  <div>
                    <strong>Phone</strong>
                    <p>
                      <a href="tel:9886332528">9886332528</a>
                    </p>
                  </div>
                </div>
                <div className="detail-item">
                  <Calendar size={20} />
                  <div>
                    <strong>Working Hours</strong>
                    <p>Monday–Saturday: 9am–8pm<br />Sunday: Closed</p>
                  </div>
                </div>
              </div>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="btn-primary">
                <Phone size={20} />
                Book Now on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>);

};

export default Home;